import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CornerDownLeft, Loader2, Search, User, type LucideIcon } from 'lucide-react';
import apiClient from '@/api/client';

// ─── CommandPalette ──────────────────────────────────────────────────────────
//
// Globale Schnellsuche (Cmd/Ctrl+K): findet Personen über /api/search und
// bietet zusätzlich alle (permissiongefilterten) Seiten als Sprungziele an.
// Personen öffnen den Stammbaum mit ausgewählter Person (/?person=<id>).

export interface PaletteNavItem {
  label: string;
  path: string;
  icon?: LucideIcon;
}

interface PersonHit {
  id: string;
  firstName: string;
  lastName: string | null;
  birthDateText: string | null;
  deathDateText: string | null;
  birthPlace: string | null;
}

interface SearchResponse {
  data: PersonHit[];
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  /** Bereits permissiongefilterte Navigationsziele. */
  navItems: PaletteNavItem[];
  /** Personen-Suche nur mit search.view anbieten. */
  canSearchPersons: boolean;
}

type PaletteEntry =
  | { kind: 'person'; person: PersonHit }
  | { kind: 'page'; item: PaletteNavItem };

function formatLifespan(p: PersonHit): string {
  const birth = p.birthDateText ? `* ${p.birthDateText}` : '';
  const death = p.deathDateText ? `† ${p.deathDateText}` : '';
  return [birth, death].filter(Boolean).join('  ');
}

export function CommandPalette({ open, onClose, navItems, canSearchPersons }: CommandPaletteProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Beim Öffnen: Zustand zurücksetzen, Eingabefeld fokussieren und beim
  // Schließen den Fokus an das auslösende Element zurückgeben.
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    setQuery('');
    setDebouncedQuery('');
    setSelectedIndex(0);
    // Nach dem Rendern fokussieren
    requestAnimationFrame(() => inputRef.current?.focus());
    return () => previouslyFocused?.focus?.();
  }, [open]);

  // Escape schließt NUR die Palette – capture-Phase mit stopPropagation,
  // damit Escape-Listener offener Modals dahinter (PersonForm, ImportModal,
  // Archiv-Detail) nicht mitfeuern. Funktioniert auch, wenn der Fokus
  // nicht mehr im Eingabefeld liegt.
  useEffect(() => {
    if (!open) return;
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener('keydown', handleEscape, true);
    return () => document.removeEventListener('keydown', handleEscape, true);
  }, [open, onClose]);

  // Debounce: 250ms nach letztem Tastendruck
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => clearTimeout(timer);
  }, [query]);

  const { data: personResult, isFetching } = useQuery<SearchResponse>({
    queryKey: ['palette-search', debouncedQuery],
    queryFn: async () => {
      const { data } = await apiClient.get<SearchResponse>('/search', {
        params: { q: debouncedQuery, page: 1, limit: 8 },
      });
      return data;
    },
    enabled: open && canSearchPersons && debouncedQuery.length >= 2,
    placeholderData: (prev) => prev,
  });

  const persons: PersonHit[] =
    canSearchPersons && debouncedQuery.length >= 2 ? (personResult?.data ?? []) : [];

  const filteredPages = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return navItems;
    return navItems.filter((item) => item.label.toLowerCase().includes(q));
  }, [navItems, query]);

  const entries = useMemo<PaletteEntry[]>(
    () => [
      ...persons.map((person): PaletteEntry => ({ kind: 'person', person })),
      ...filteredPages.map((item): PaletteEntry => ({ kind: 'page', item })),
    ],
    [persons, filteredPages],
  );

  // Auswahl zurücksetzen, wenn sich die Trefferliste ändert.
  useEffect(() => {
    setSelectedIndex(0);
  }, [debouncedQuery, entries.length]);

  // Ausgewähltes Element sichtbar halten.
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${selectedIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  if (!open) return null;

  const activate = (entry: PaletteEntry) => {
    if (entry.kind === 'person') {
      navigate(`/?person=${entry.person.id}`);
    } else {
      navigate(entry.item.path);
    }
    onClose();
  };

  // stopPropagation: Pfeiltasten/Enter steuern nur die Palette und dürfen
  // keine Tastatur-Listener der Seite dahinter auslösen. Escape wird
  // separat über den capture-Listener oben behandelt.
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      e.stopPropagation();
      setSelectedIndex((i) => (entries.length ? (i + 1) % entries.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      setSelectedIndex((i) => (entries.length ? (i - 1 + entries.length) % entries.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      const entry = entries[selectedIndex];
      if (entry) activate(entry);
    }
  };

  const personStartIndex = 0;
  const pageStartIndex = persons.length;
  const showEmptyState =
    query.trim().length > 0 && !isFetching && entries.length === 0;

  return (
    <div
      className="fixed inset-0 z-[1450] flex items-start justify-center bg-black/40 p-4 pt-[12vh]"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Schnellsuche"
    >
      <div
        className="w-full max-w-lg overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Eingabezeile */}
        <div className="flex items-center gap-2.5 border-b border-slate-200 px-4 py-3">
          {isFetching ? (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin text-slate-400" aria-hidden="true" />
          ) : (
            <Search className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
          )}
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={canSearchPersons ? 'Person suchen oder Seite öffnen…' : 'Seite öffnen…'}
            aria-label="Schnellsuche"
            role="combobox"
            aria-expanded="true"
            aria-controls="command-palette-listbox"
            aria-autocomplete="list"
            aria-activedescendant={
              entries.length > 0 ? `command-palette-option-${selectedIndex}` : undefined
            }
            className="w-full bg-transparent text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
          />
          <kbd className="hidden rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium text-slate-400 sm:inline-block">
            esc
          </kbd>
        </div>

        {/* Trefferliste */}
        <div
          ref={listRef}
          id="command-palette-listbox"
          className="max-h-80 overflow-y-auto py-1.5"
          role="listbox"
          aria-label="Suchergebnisse"
        >
          {persons.length > 0 && (
            <>
              <p
                role="presentation"
                aria-hidden="true"
                className="px-4 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400"
              >
                Personen
              </p>
              {persons.map((person, i) => {
                const index = personStartIndex + i;
                const isSelected = index === selectedIndex;
                const fullName = `${person.firstName} ${person.lastName ?? ''}`.trim();
                const lifespan = formatLifespan(person);
                return (
                  <button
                    key={person.id}
                    id={`command-palette-option-${index}`}
                    data-index={index}
                    role="option"
                    aria-selected={isSelected}
                    onMouseEnter={() => setSelectedIndex(index)}
                    onClick={() => activate({ kind: 'person', person })}
                    className={`flex w-full items-center gap-3 px-4 py-2 text-left text-sm ${
                      isSelected ? 'bg-blue-50 text-blue-700' : 'text-slate-700'
                    }`}
                  >
                    <User
                      className={`h-4 w-4 shrink-0 ${isSelected ? 'text-blue-500' : 'text-slate-400'}`}
                      aria-hidden="true"
                    />
                    <span className="truncate font-medium">{fullName}</span>
                    {(lifespan || person.birthPlace) && (
                      <span className="ml-auto shrink-0 text-xs text-slate-400">
                        {lifespan || person.birthPlace}
                      </span>
                    )}
                  </button>
                );
              })}
            </>
          )}

          {filteredPages.length > 0 && (
            <>
              <p
                role="presentation"
                aria-hidden="true"
                className="px-4 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400"
              >
                Seiten
              </p>
              {filteredPages.map((item, i) => {
                const index = pageStartIndex + i;
                const isSelected = index === selectedIndex;
                return (
                  <button
                    key={item.path}
                    id={`command-palette-option-${index}`}
                    data-index={index}
                    role="option"
                    aria-selected={isSelected}
                    onMouseEnter={() => setSelectedIndex(index)}
                    onClick={() => activate({ kind: 'page', item })}
                    className={`flex w-full items-center gap-3 px-4 py-2 text-left text-sm ${
                      isSelected ? 'bg-blue-50 text-blue-700' : 'text-slate-700'
                    }`}
                  >
                    {item.icon && (
                      <item.icon
                        className={`h-4 w-4 shrink-0 ${isSelected ? 'text-blue-500' : 'text-slate-400'}`}
                        aria-hidden="true"
                      />
                    )}
                    <span className="truncate">{item.label}</span>
                  </button>
                );
              })}
            </>
          )}

          {showEmptyState && (
            <p className="px-4 py-6 text-center text-sm text-slate-400">Keine Treffer.</p>
          )}
        </div>

        {/* Fußzeile mit Tastatur-Hinweisen */}
        <div className="flex items-center gap-3 border-t border-slate-100 px-4 py-2 text-[11px] text-slate-400">
          <span className="flex items-center gap-1">
            <kbd className="rounded border border-slate-200 bg-slate-50 px-1 py-0.5 font-mono">↑↓</kbd>
            navigieren
          </span>
          <span className="flex items-center gap-1">
            <kbd className="rounded border border-slate-200 bg-slate-50 px-1 py-0.5">
              <CornerDownLeft className="h-3 w-3" aria-hidden="true" />
            </kbd>
            öffnen
          </span>
        </div>
      </div>
    </div>
  );
}
